import React, { useEffect, useState } from 'react';
import { Card, Button, ErrorNote } from '../ui';

interface SetupInfo {
  dataDir: string;
  db: { host: string; port: number; database: string; user: string; configured: boolean };
  controlPort: number;
}

export function SettingsScreen() {
  const [setup, setSetup] = useState<SetupInfo | null>(null);
  const [secretRefs, setSecretRefs] = useState<{ name: string; kind: string; maskedHint: string }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [engineMsg, setEngineMsg] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      setSetup((await window.pagepilot.setup.get()) as SetupInfo);
      try {
        setSecretRefs((await window.pagepilot.secrets.list()) as typeof secretRefs);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
  }, []);

  return (
    <div className="max-w-3xl space-y-4">
      <h1 className="text-lg font-semibold">Settings</h1>
      {error && <ErrorNote message={error} />}

      <Card title="Storage & database">
        <dl className="space-y-1.5 text-sm">
          <Row label="Data folder" value={setup?.dataDir ?? '—'} />
          <Row label="PostgreSQL" value={setup ? `${setup.db.user}@${setup.db.host}:${setup.db.port}/${setup.db.database}` : '—'} />
          <Row label="Password" value={setup?.db.configured ? '•••••••• (DPAPI encrypted)' : 'not configured'} />
          <Row label="Engine control port" value={setup ? `127.0.0.1:${setup.controlPort}` : '—'} />
        </dl>
      </Card>

      <Card title="Stored secrets">
        {secretRefs.length === 0 ? (
          <p className="text-xs text-neutral-500">No secrets stored yet. Page tokens and API keys appear here with masked hints only.</p>
        ) : (
          <ul className="space-y-1 text-xs">
            {secretRefs.map((s) => (
              <li key={s.name} className="flex gap-3">
                <span className="w-56 font-mono text-neutral-400">{s.name}</span>
                <span className="w-40 text-neutral-500">{s.kind}</span>
                <span className="font-mono text-neutral-500">{s.maskedHint}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card title="Background engine">
        <p className="mb-3 text-xs text-neutral-500">
          The engine runs independently of this window. Closing the window minimizes PagePilot to the tray and keeps
          scheduling and publishing active. The engine cannot run while the computer is shut down.
        </p>
        {engineMsg && <p className="mb-2 text-xs text-emerald-400">{engineMsg}</p>}
        <div className="flex gap-2">
          <Button
            variant="ghost"
            onClick={async () => {
              const r = await window.pagepilot.engine.ensureStarted();
              setEngineMsg(`Engine: ${String(r)}`);
            }}
          >
            Start / attach engine
          </Button>
          <Button variant="ghost" onClick={async () => { await window.pagepilot.engine.pause(); setEngineMsg('Automation paused.'); }}>
            Pause automation
          </Button>
          <Button variant="ghost" onClick={async () => { await window.pagepilot.engine.resume(); setEngineMsg('Automation resumed.'); }}>
            Resume automation
          </Button>
          <Button variant="danger" onClick={() => void window.pagepilot.app.quitCompletely()}>
            Quit PagePilot AI completely
          </Button>
        </div>
      </Card>
    </div>
  );
}

function Row(props: { label: string; value: string }) {
  return (
    <div className="flex gap-3 text-sm">
      <dt className="w-44 shrink-0 text-neutral-500">{props.label}</dt>
      <dd className="truncate font-mono text-xs leading-6 text-neutral-300">{props.value}</dd>
    </div>
  );
}
